import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { adminApi } from '@/api';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  Modal,
  ProgressBar,
  useToast,
} from '@/components/ui';
import { AccountActions } from '@/features/admin/AccountActions';
import { AssignPlanAction } from '@/features/admin/AssignPlanAction';
import { CustomerInvoices } from '@/features/billing/CustomerInvoices';
import { SignInAsAction } from '@/features/admin/SignInAsAction';
import { VerifyEmailAction } from '@/features/admin/VerifyEmailAction';
import { Fact, FactList, Prose } from '@/features/admin/Facts';
import { RecordHeader } from '@/features/admin/RecordHeader';
import { CompanyLogo } from '@/features/internships/CompanyLogo';
import {
  ACCOUNT_TONES,
  INTERNSHIP_TONES,
  SUBSCRIPTION_TONES,
  VERIFICATION_TONES,
  humanizeStatus,
  roleLabel,
} from '@/features/admin/status';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { formatDate, humanizeWorkMode } from '@/utils/format';
import { formatPhone } from '@/utils/phone';

/** One company, whole. The decisions live here too, next to what informs them. */
export function AdminCompanyDetailPage() {
  const { id } = useParams();
  return <AdminCompanyRecord companyId={id} />;
}

/**
 * The whole company record for one id, decisions included. `embedded` drops
 * the way back to the companies list, for when the record is a tab inside
 * another record (an application) rather than a page of its own.
 */
export function AdminCompanyRecord({ companyId: id, embedded = false }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['admin-company', id],
    queryFn: () => adminApi.getCompany(id),
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['admin-company', id] });
    void queryClient.invalidateQueries({ queryKey: ['admin-companies'] });
    void queryClient.invalidateQueries({ queryKey: ['admin-verifications'] });
    void queryClient.invalidateQueries({ queryKey: ['admin-overview'] });
  }

  const approve = useMutation({
    mutationFn: () => adminApi.approveCompany(id),
    onSuccess: () => {
      toast.success('Company verified');
      invalidate();
    },
    onError: (approveError) =>
      toast.error(errorMessage(approveError, 'Could not verify the company')),
  });

  const reject = useMutation({
    mutationFn: (text) => adminApi.rejectCompany(id, text),
    onSuccess: () => {
      toast.success('Verification rejected');
      invalidate();
      setRejecting(false);
      setReason('');
    },
    onError: (rejectError) =>
      toast.error(errorMessage(rejectError, 'Could not reject the request')),
  });

  const feature = useMutation({
    mutationFn: (isFeatured) => adminApi.setCompanyFeatured(id, isFeatured),
    onSuccess: () => {
      toast.success('Featured companies updated');
      invalidate();
    },
    onError: (featureError) =>
      toast.error(errorMessage(featureError, 'Could not update featuring')),
  });

  const restore = useMutation({
    mutationFn: () => adminApi.restoreCompany(id),
    onSuccess: (result) => {
      const count = result?.logins_restored ?? 0;
      toast.success(
        `Company restored. ${count} ${count === 1 ? 'login was' : 'logins were'} switched back on.`,
      );
      invalidate();
    },
    onError: (restoreError) =>
      toast.error(errorMessage(restoreError, 'Could not restore the company')),
  });

  if (isPending) return <LoadingState label="Loading the company…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  const isVerified = data.verification_status === 'VERIFIED';
  // Only a request the company has actually submitted can be decided. Before
  // that (UNVERIFIED) there is nothing to judge, and after a rejection the
  // next decision waits for the company to send it again.
  const isAwaitingDecision = data.verification_status === 'PENDING';
  // A closed company is read-only here apart from restoring it: verifying or
  // featuring something nobody can see would do nothing.
  const isClosed = Boolean(data.closed_at);
  const pipeline = Object.entries(data.applications_by_status ?? {}).filter(
    ([, count]) => count > 0,
  );

  return (
    <div>
      <RecordHeader
        backTo={embedded ? undefined : paths.admin.companies}
        backLabel="Companies"
        eyebrow="Company"
        title={data.name}
        leading={<CompanyLogo company={data} className="size-11 rounded-xl" iconSize={20} />}
        subtitle={[data.industry, data.location, data.company_size].filter(Boolean).join(' · ')}
        badges={
          <>
            <Badge tone={VERIFICATION_TONES[data.verification_status] ?? 'neutral'}>
              {humanizeStatus(data.verification_status)}
            </Badge>
            {isClosed && <Badge tone="danger">Closed</Badge>}
            {data.is_featured && <Badge tone="brand">Featured</Badge>}
            {data.verified_at && (
              <span className="text-ink-muted text-xs">
                Verified {formatDate(data.verified_at)}
              </span>
            )}
            {/* Why there is no Verify / Reject up in the actions. */}
            {!isClosed && data.verification_status === 'UNVERIFIED' && (
              <span className="text-ink-muted text-xs">Not submitted for verification yet</span>
            )}
            {!isClosed && data.verification_status === 'REJECTED' && (
              <span className="text-ink-muted text-xs">Waiting for the company to resubmit</span>
            )}
          </>
        }
        actions={
          <>
            <Link
              to={paths.companyPublic(data.slug)}
              className="text-brand-700 text-sm hover:underline"
            >
              Public page
            </Link>
            {isClosed ? (
              <Button
                variant="success"
                size="sm"
                isLoading={restore.isPending}
                onClick={() => restore.mutate()}
              >
                Restore company
              </Button>
            ) : isVerified ? (
              <Button
                variant="secondary"
                size="sm"
                isLoading={feature.isPending}
                onClick={() => feature.mutate(!data.is_featured)}
              >
                {data.is_featured ? 'Unfeature' : 'Feature'}
              </Button>
            ) : isAwaitingDecision ? (
              <>
                <Button
                  variant="success"
                  size="sm"
                  isLoading={approve.isPending}
                  onClick={() => approve.mutate()}
                >
                  Verify
                </Button>
                <Button variant="secondary" size="sm" onClick={() => setRejecting(true)}>
                  Reject
                </Button>
              </>
            ) : null}
          </>
        }
        facts={[
          {
            label: 'Internships',
            value: data.internship_count,
            hint: `${data.published_internship_count} published`,
          },
          { label: 'Applications', value: data.application_count },
          { label: 'Team', value: data.member_count, hint: 'Active members' },
          {
            label: 'Joined',
            value: formatDate(data.created_at),
            hint: `Last edited ${formatDate(data.updated_at)}`,
          },
        ]}
      />

      {isClosed && (
        <Alert
          tone="warning"
          className="mb-6"
          title={`Closed by the owner on ${formatDate(data.closed_at)}`}
        >
          The company is hidden everywhere and its team cannot sign in. Restoring reopens it and
          switches back on the logins the closure turned off. Listings and applications stay closed:
          candidates were told they ended.
        </Alert>
      )}

      {/* What the company was told, so the next admin to open it knows why. */}
      {!isClosed &&
        data.verification_status === 'REJECTED' &&
        data.verification_rejection_reason && (
          <Alert
            tone="error"
            className="mb-6"
            title={
              data.verification_rejected_at
                ? `Verification rejected on ${formatDate(data.verification_rejected_at)}`
                : 'Verification rejected'
            }
          >
            <span className="font-medium">Reason sent to the company:</span>{' '}
            {data.verification_rejection_reason}
          </Alert>
        )}

      {/* Suspending or banning the owner is what blocks a company: the API then
          hides its page and every listing from students. */}
      {!isClosed && ['SUSPENDED', 'BANNED'].includes(data.owner_status) && (
        <Alert
          tone="error"
          className="mb-6"
          title={`Blocked — the owner is ${data.owner_status === 'BANNED' ? 'banned' : 'suspended'}`}
        >
          Students cannot see the company page or any of its listings, and nobody can apply. Nothing
          has been changed or closed. Reinstate the owner&apos;s account below to show everything
          again.
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="min-w-0 space-y-6">
          {/* What the company sent with its last request -- the registration
              number is what the admin checks against public records. */}
          {data.verification_status !== 'UNVERIFIED' && (
            <Card>
              <CardHeader
                title="Verification request"
                description="What the company sent with its last request."
              />
              <FactList>
                <Fact label="Registration number" value={data.registration_number} showEmpty />
                <Fact
                  label="Note from the company"
                  showEmpty
                  value={
                    data.verification_note ? (
                      <span className="whitespace-pre-line">{data.verification_note}</span>
                    ) : null
                  }
                />
              </FactList>
            </Card>
          )}

          <Card>
            <CardHeader title="Profile" />
            <FactList>
              <Fact label="Industry" value={data.industry} />
              <Fact label="Company size" value={data.company_size} />
              <Fact label="Founded" value={data.founded_year} />
              <Fact label="Location" value={data.location} />
              <Fact label="Contact email" value={data.contact_email} />
              <Fact label="Contact phone" value={formatPhone(data.contact_phone)} />
              <Fact label="Slug" value={data.slug} />
            </FactList>

            <div className="mt-4 flex flex-col gap-1 text-sm">
              {[data.website, data.linkedin_url].filter(Boolean).map((href) => (
                <a
                  key={href}
                  href={href}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-brand-700 break-all hover:underline"
                >
                  {href}
                </a>
              ))}
            </div>

            <Prose>{data.description}</Prose>

            <ProgressBar
              className="mt-4"
              value={data.profile_completion}
              label="Profile completion"
            />
          </Card>

          <Card>
            <CardHeader
              title="Internships"
              description={
                data.internships.length < data.internship_count
                  ? `The ${data.internships.length} most recent of ${data.internship_count}.`
                  : undefined
              }
            />
            {data.internships.length === 0 ? (
              <EmptyState compact title="This company has not posted anything yet" />
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table w-full text-sm">
                  <thead className="text-ink-muted text-left">
                    <tr className="border-line border-b">
                      <th scope="col" className="py-2 pr-4 font-medium">
                        Title
                      </th>
                      <th scope="col" className="py-2 pr-4 font-medium">
                        Status
                      </th>
                      <th scope="col" className="py-2 pr-4 text-right font-medium">
                        Applications
                      </th>
                      <th scope="col" className="py-2 pr-4 text-right font-medium">
                        Views
                      </th>
                      <th scope="col" className="py-2 pr-4 font-medium">
                        Posted
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.internships.map((row) => (
                      <tr key={row.id} className="border-line border-b last:border-0">
                        <td className="py-3 pr-4">
                          <Link
                            to={paths.admin.internshipDetail(row.id)}
                            className="text-ink font-medium hover:underline"
                          >
                            {row.title}
                          </Link>
                          <span className="text-ink-muted block text-xs">
                            {[row.location, row.work_mode ? humanizeWorkMode(row.work_mode) : null]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                        </td>
                        <td className="py-3 pr-4">
                          <Badge tone={INTERNSHIP_TONES[row.status] ?? 'neutral'}>
                            {humanizeStatus(row.status)}
                          </Badge>
                        </td>
                        <td className="text-ink-muted py-3 pr-4 text-right tabular-nums">
                          {row.applications_count}
                        </td>
                        <td className="text-ink-muted py-3 pr-4 text-right tabular-nums">
                          {row.views_count}
                        </td>
                        <td className="text-ink-muted py-3 pr-4 whitespace-nowrap">
                          {formatDate(row.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <CustomerInvoices companyId={data.id} />
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Owner" />
            <p className="text-ink text-sm font-medium">{data.owner_name ?? '—'}</p>
            <p className="text-ink-muted text-sm break-all">{data.owner_email}</p>
            <div className="mt-2 flex items-center gap-2">
              {data.owner_status && (
                <Badge tone={ACCOUNT_TONES[data.owner_status] ?? 'neutral'}>
                  {humanizeStatus(data.owner_status)}
                </Badge>
              )}
              <span className="text-ink-muted text-xs">
                {data.owner_last_login_at
                  ? `Last seen ${formatDate(data.owner_last_login_at)}`
                  : 'Never signed in'}
              </span>
            </div>

            {/* Moderating the company is verification and featuring, up in the
                masthead. This is the person's own account, which is a
                different decision with different consequences. */}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <AccountActions
                account={{
                  id: data.owner_user_id,
                  full_name: data.owner_name,
                  status: data.owner_status,
                  role: 'COMPANY',
                }}
                className="flex flex-wrap gap-2"
              />
              {/* Signing in as the owner is how a workspace problem gets
                  reproduced -- their seat is what carries the workspace role. */}
              <VerifyEmailAction
                account={{
                  id: data.owner_user_id,
                  full_name: data.owner_name,
                  status: data.owner_status,
                  role: 'COMPANY',
                }}
              />
              <SignInAsAction
                account={{
                  id: data.owner_user_id,
                  full_name: data.owner_name,
                  status: data.owner_status,
                  role: 'COMPANY',
                }}
              />
            </div>
          </Card>

          <Card>
            <CardHeader
              title="Team"
              action={
                <Link
                  to={`${paths.admin.recruiters}?company_id=${data.id}`}
                  className="text-brand-700 text-sm hover:underline"
                >
                  All seats
                </Link>
              }
            />
            <ol className="space-y-3">
              {data.members.map((member) => (
                <li key={member.id} className="border-line border-b pb-3 last:border-0 last:pb-0">
                  <p className="text-ink text-sm font-medium">
                    {/* The member id *is* the seat id, so a roster row opens
                        the recruiter record without another lookup. */}
                    <Link to={paths.admin.recruiterDetail(member.id)} className="hover:underline">
                      {member.full_name}
                    </Link>
                    {member.is_owner && (
                      <Badge tone="brand" className="ml-2">
                        Owner
                      </Badge>
                    )}
                  </p>
                  <p className="text-ink-muted text-xs break-all">{member.email}</p>
                  <p className="text-ink-muted mt-1 text-xs">
                    {roleLabel(member.role)}
                    {member.job_title ? ` · ${member.job_title}` : ''}
                    {member.status !== 'ACTIVE' ? ` · ${member.status.toLowerCase()}` : ''}
                  </p>
                </li>
              ))}
            </ol>
          </Card>

          <Card>
            <CardHeader title="Subscription" />
            {data.subscription ? (
              <>
                <p className="text-ink flex items-center gap-2 text-sm font-medium">
                  <Link
                    to={paths.admin.subscriptionDetail(data.subscription.id)}
                    className="hover:underline"
                  >
                    {data.subscription.plan_name}
                  </Link>
                  <Badge tone={SUBSCRIPTION_TONES[data.subscription.status] ?? 'neutral'}>
                    {humanizeStatus(data.subscription.status)}
                  </Badge>
                </p>
                <p className="text-ink-muted mt-1 text-xs">
                  Renews {formatDate(data.subscription.current_period_end)}
                  {data.subscription.cancel_at_period_end ? ' · cancels at period end' : ''}
                </p>
              </>
            ) : (
              /* No row is the free plan, not missing data -- say so. */
              <p className="text-ink-muted text-sm">On the free plan.</p>
            )}

            {/* A free-plan company has no subscription row, so it never
                appears on the subscriptions roster. This is the only place an
                admin can sell to one. */}
            <div className="mt-4">
              <AssignPlanAction
                companyId={data.id}
                companyName={data.name}
                hasSubscription={Boolean(data.subscription)}
              />
            </div>
          </Card>

          <Card>
            <CardHeader title="Application pipeline" />
            {pipeline.length === 0 ? (
              <EmptyState compact title="No applications yet" />
            ) : (
              <dl className="space-y-1.5 text-sm">
                {pipeline.map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between gap-3">
                    <dt className="text-ink-muted">{humanizeStatus(status)}</dt>
                    <dd className="text-ink font-medium tabular-nums">{count}</dd>
                  </div>
                ))}
              </dl>
            )}
          </Card>
        </div>
      </div>

      <Modal
        open={rejecting}
        title={`Reject ${data.name}`}
        description="The reason is recorded in the audit log and sent to the company."
        onClose={() => setRejecting(false)}
      >
        <div className="space-y-4">
          <Input
            label="Reason"
            required
            placeholder="Registration number does not match public records"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setRejecting(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={reason.trim().length < 4}
              isLoading={reject.isPending}
              onClick={() => reject.mutate(reason.trim())}
            >
              Reject
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
